import "./index.css";
import "handsontable/dist/handsontable.full.min.css";
import {
  PublicClientApplication,
  EventType,
  EventMessage,
  AuthenticationResult,
} from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { ChakraProvider } from "@chakra-ui/react";
import {
  init as SentryInit,
  ErrorBoundary as SentryErrorBoundary,
} from "@sentry/react";
import { registerAllModules } from "handsontable/registry";
import React from "react";
import { AliveScope } from "react-activation";
import ReactDOM from "react-dom/client";
import { hotjar } from "react-hotjar";
import { Provider } from "react-redux";
import {
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";

import { analyticsService } from "@/utils/analytics-service.ts";
import {
  getRegionFromUrl,
  isRegionSelectionPending,
} from "@/utils/baseUrls.ts";

import App from "./App.tsx";
import { loginRequest, msalConfig } from "./auth/authConfig";
import { GenericErrorFallback } from "./components/error/generic-error.tsx";
import { InitLoader } from "./components/loaders/InitLoader.tsx";
import neoTheme from "./design/theme";
import { worker } from "./mocks/browser.ts";
import { PreAuthRouter } from "./routes/login-routes.tsx";
import { store, persistor } from "./store";
import { cacheUtil, CacheKey } from "./utils/localstorage.ts";

registerAllModules();
/*
if (process.env.NODE_ENV === "development") {
  worker
    .start({
      onUnhandledRequest: "bypass",
    })
    .catch((err) => {
      console.log(err);
    });
}
*/

if (import.meta.env.VITE_HOTJAR_ENABLED === "true") {
  hotjar.initialize({
    id: parseInt(import.meta.env.VITE_HOTJAR_ID as string, 10),
    sv: parseInt(import.meta.env.VITE_HOTJAR_VERSION as string, 10),
  });
}

SentryInit({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: process.env.NODE_ENV === "production",
  // integrations: [
  //   new BrowserTracing({
  //     routingInstrumentation: reactRouterV6Instrumentation(
  //       React.useEffect,
  //       useLocation,
  //       useNavigationType,
  //       createRoutesFromChildren,
  //       matchRoutes
  //     ),
  //   }),
  // ],
  normalizeDepth: 11,
  // Performance Monitoring
  tracesSampleRate: 0.2,
});

export const msalInstance = new PublicClientApplication(msalConfig);

const handleLoginRedirect = () => {
  if (isRegionSelectionPending()) return;
  const loginHint = window.location.search.split("login_hint=")[1] ?? "";
  cacheUtil.removeAll();
  msalInstance
    .loginRedirect({
      ...loginRequest,
      loginHint,
      extraQueryParameters: { region: getRegionFromUrl() },
    })
    .catch((err) => console.log(err));
};

msalInstance
  .initialize()
  .then(() => {
    const activeAccount = msalInstance.getActiveAccount();
    if (activeAccount) {
      analyticsService.init();
      analyticsService.identifyUser({ userId: activeAccount.username });
    }
    // Default to using the first account if no account is active on page load
    if (!activeAccount && msalInstance.getAllAccounts().length > 0) {
      // Account selection logic is app dependent. Adjust as needed for different use cases.
      const account = msalInstance.getAllAccounts()[0];
      const tokenRequest = {
        scopes: loginRequest.scopes,
        loginHint: account.username,
        account,
      };
      analyticsService.init();
      analyticsService.identifyUser({ userId: account.username });

      msalInstance.setActiveAccount(account);
      void msalInstance.acquireTokenSilent(tokenRequest);
    }

    msalInstance.enableAccountStorageEvents();

    msalInstance.addEventCallback((event: EventMessage) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        event.eventType === EventType.SSO_SILENT_SUCCESS
      ) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        analyticsService.init();
        analyticsService.identifyUser({ userId: account.username });
        msalInstance.setActiveAccount(account);

        if (process.env.NODE_ENV === "development") {
          cacheUtil.set(CacheKey.TenantId, "test_tenant_2");
          console.log("Local dev TenantId set to test_tenant_2");
        }
      }
    });

    msalInstance
      .handleRedirectPromise()
      .then((_) => {
        // Check if user signed in
        const account = msalInstance.getActiveAccount();
        if (!account) {
          // redirect anonymous user to login page
          handleLoginRedirect();
          analyticsService.clearUser();
        }
      })
      .catch((err) => {
        handleLoginRedirect();
        console.log(err);
      });
  })
  .catch((err) => {
    console.log(err);
  });

export const root = ReactDOM.createRoot(document.getElementById("root")!);

root.render(
  <React.StrictMode>
    <SentryErrorBoundary fallback={GenericErrorFallback}>
      {!isRegionSelectionPending() ? (
        <MsalProvider instance={msalInstance}>
          <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <ChakraProvider cssVarsRoot="body" theme={neoTheme}>
                <AliveScope>
                  <App />
                </AliveScope>
              </ChakraProvider>
            </PersistGate>
          </Provider>
        </MsalProvider>
      ) : (
        <RouterProvider
          router={PreAuthRouter}
          fallbackElement={<InitLoader />}
        />
      )}
    </SentryErrorBoundary>
  </React.StrictMode>
);
